import { Component } from 'react';

// Lazy chunk fetch failures (e.g. a deploy in progress) usually clear themselves
// on the next attempt, so auto-reload instead of making the visitor stare at a screen.
// Also catches the case where the server answers a stale chunk request with
// index.html (SPA fallback): the import() then throws a plain SyntaxError that
// mentions the chunk filename, and a manual refresh revalidates index.html and self-heals.
const isChunkError = (error) =>
  error &&
  (error.name === 'ChunkLoadError' ||
    /loading chunk|dynamically imported module|import\(\)|Loading CSS chunk/i.test(
      String(error && error.message)
    ) ||
    (error instanceof SyntaxError &&
      /assets\/.+\.js/.test(String(error && error.message))));

const AUTO_RELOAD_DELAY = 4000;
// Never trap a visitor in a reload loop: if we auto-reloaded moments ago and the
// error is back, stop and leave the branded fallback on screen.
const MIN_SECONDS_BETWEEN_AUTO_RELOADS = 15;

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, chunkError: false };
    this.reloadTimer = null;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, chunkError: isChunkError(error) };
  }

  componentDidCatch(error, errorInfo) {
    // Keep the real error visible in the console for debugging.
    console.error('[Cherry Hills] Page crashed — showing fallback:', error, errorInfo);
  }

  componentDidMount() {
    // Chunk errors can surface mid-session (a navigation loads a stale chunk),
    // after this hook has already run — scheduling happens in componentDidUpdate.
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.chunkError && !prevState.chunkError) {
      let last = 0;
      try {
        last = parseInt(sessionStorage.getItem('ch-last-chunk-reload') || '0', 10) || 0;
      } catch {
        last = 0;
      }
      const cooledDown = Date.now() - last > MIN_SECONDS_BETWEEN_AUTO_RELOADS * 1000;
      if (cooledDown) {
        this.reloadTimer = setTimeout(() => {
          try {
            sessionStorage.setItem('ch-last-chunk-reload', String(Date.now()));
          } catch {
            /* ignore */
          }
          window.location.reload();
        }, AUTO_RELOAD_DELAY);
      }
    }
  }

  componentWillUnmount() {
    if (this.reloadTimer) clearTimeout(this.reloadTimer);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    const base = typeof __BASE_PATH__ === 'string' ? __BASE_PATH__ : '';
    window.location.href = window.location.origin + base + '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const base = typeof __BASE_PATH__ === 'string' ? __BASE_PATH__ : '';
    const homeHref = window.location.origin + base + '/';

    return (
      <div className="fixed inset-0 z-[10000] bg-[#0A1628] flex flex-col items-center justify-center px-6 text-center overflow-y-auto">
        {/* Wordmark */}
        <div className="font-serif font-bold text-2xl sm:text-3xl select-none">
          <span style={{ color: '#D9A441' }}>CHERRY</span>{' '}
          <span style={{ color: '#2D9F5D' }}>HILLS</span>
        </div>
        <p className="mt-2 text-[10px] sm:text-xs font-bold tracking-[0.34em] uppercase text-[#8EA0B5]">
          Portfolio Management Ltd.
        </p>

        {/* Icon */}
        <div className="mt-10 w-16 h-16 rounded-full bg-[#F2652D]/10 border border-[#F2652D]/30 flex items-center justify-center">
          <i className="ri-refresh-line text-2xl text-[#F2652D]" aria-hidden="true" />
        </div>

        <h1 className="mt-6 text-xl sm:text-2xl font-bold text-white">
          {this.state.chunkError ? 'We couldn\u2019t load this page' : 'Something went wrong'}
        </h1>

        <p className="mt-3 max-w-md text-xs sm:text-sm leading-relaxed text-[#9FB0C3]">
          {this.state.chunkError ? (
            <>
              A network hiccup interrupted the page while it was loading. Don\u2019t worry — we
              are retrying automatically.
            </>
          ) : (
            <>
              An unexpected error interrupted this page. Please reload — if it keeps happening,
              reach out to us at{' '}
              <a
                href="mailto:info@cherryhillsportfolio.com"
                className="text-[#F2652D] hover:underline"
              >
                info@cherryhillsportfolio.com
              </a>
              .
            </>
          )}
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={this.handleReload}
            className="px-8 py-3 rounded-full bg-[#F2652D] hover:bg-[#D95A28] text-white text-xs font-bold uppercase tracking-widest transition-colors cursor-pointer"
          >
            Reload page
          </button>
          <a
            href={homeHref}
            className="px-8 py-3 rounded-full border border-white/20 hover:border-[#D9A441] hover:text-[#D9A441] text-white text-xs font-bold uppercase tracking-widest transition-colors"
          >
            Go to homepage
          </a>
        </div>

        {this.state.chunkError && (
          <p className="mt-6 text-[10px] uppercase tracking-widest text-[#5C6E82] animate-pulse">
            Retrying automatically…
          </p>
        )}
      </div>
    );
  }
}

export default ErrorBoundary;